/* eslint-disable @typescript-eslint/no-non-null-assertion */
import clsx from "clsx";
import React from "react";
import { Sprinkles } from "web/react/emo/sprinkles.css";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import * as styles from "./curved-pill.css";

export interface CurvedPillProps {
    label?: string;
    onClick: () => void;
    active: boolean;
    activeHighlighted: boolean;
    deselected?: boolean;
    disabled?: boolean;
    icon?: JSX.Element;
    className?: string;
    href?: string;
    ariaLabel?: string;
    as?: "button" | "div";
    width?: Sprinkles["width"];
    height?: Sprinkles["height"];
    variant?: "primary" | "secondary" | "tertiary";
}

export function CurvedPill({
    label,
    onClick,
    active,
    activeHighlighted,
    deselected = false,
    disabled = false,
    icon,
    className,
    href,
    ariaLabel,
    as = "button",
    width = "fit-content",
    height = "full",
    variant = "primary",
}: CurvedPillProps): JSX.Element {
    const contents = (
        <View
            paddingLeft={label ? "xxs" : "xs"}
            paddingX="xs"
            paddingY="xxs"
            borderRadius="xm"
            width={width}
            height={height}
            className={clsx(styles.initial, {
                [styles.active]: active,
                [styles.activeGray]: variant === "tertiary",
                [styles.inactive]: disabled || (deselected && !active),
                [styles.activeHighlighted]: variant === "primary" && activeHighlighted && active,
                [styles.activeHighlightedDark]:
                    variant === "secondary" && activeHighlighted && active,
            })}
        >
            {label && (
                <Text
                    textStyle={variant === "secondary" ? "body-xs" : "body-s"}
                    className={styles.text}
                    aria-label={label}
                >
                    {label}
                </Text>
            )}
            {icon}
        </View>
    );

    if (href) {
        return (
            <a
                aria-disabled={disabled || undefined}
                aria-label={ariaLabel}
                className={clsx(styles.control, className, { [styles.disabled]: disabled })}
                href={href}
                onClick={(event) => {
                    event.preventDefault();

                    if (!disabled) {
                        onClick();
                    }
                }}
                tabIndex={disabled ? -1 : undefined}
            >
                {contents}
            </a>
        );
    }

    // Some existing consumers already provide the interactive parent, such as a linked rail item.
    if (as === "div") {
        return (
            <div onClick={onClick} className={clsx(className, { [styles.disabled]: disabled })}>
                {contents}
            </div>
        );
    }

    return (
        <button
            aria-label={ariaLabel}
            className={clsx(styles.control, className, { [styles.disabled]: disabled })}
            disabled={disabled}
            onClick={onClick}
            type="button"
        >
            {contents}
        </button>
    );
}
